import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { AppContext } from '../../../../../context';
import apis from '../../../../../apis';

interface EsgFundImportProps {
  formData: Record<string, unknown>;
  onApplied: (patch: Record<string, unknown>) => void;
}

const SUCCESS_PERSIST_MSG = 'Applied. Save the factsheet to persist.';

function tableSignature(table: unknown): string {
  if (!Array.isArray(table)) return '[]';
  return JSON.stringify(
    table.map((row: Record<string, unknown>) => ({
      label: row?.label ?? '',
      no_turnover: row?.no_turnover ?? '',
      sales_volume: row?.sales_volume ?? '',
      esg_key: row?.esg_key ?? '',
    }))
  );
}

/** Inline ESG import controls (nfi_esg CSV) — render inside Controversial Business Areas card. */
export const EsgFundImport: FC<EsgFundImportProps> = ({ formData, onApplied }) => {
  const { document } = useContext(AppContext);
  const [valuationDates, setValuationDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    (formData.esg_valuation_date as string) || ''
  );
  const [loading, setLoading] = useState(false);
  const [datesLoading, setDatesLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const appliedBaselineRef = useRef<{ valuationDate: string; tableSig: string } | null>(null);

  const isin = useMemo(() => {
    const stammdaten = formData.stammdaten as Record<string, unknown> | undefined;
    const fromStammdaten = stammdaten?.isin;
    if (typeof fromStammdaten === 'string' && fromStammdaten.trim()) {
      return fromStammdaten.trim();
    }
    const templateIsins = document?.template?.isins;
    if (Array.isArray(templateIsins) && templateIsins[0]) {
      return String(templateIsins[0]).trim();
    }
    return '';
  }, [formData.stammdaten, document?.template?.isins]);

  const exposureFlags = (formData.esg_exposure_flags as { key: string; label: string }[]) || [];

  const loadDates = useCallback(async () => {
    if (!isin) return;
    setDatesLoading(true);
    setMessage(null);
    try {
      const res = await apis.esgFund.getValuationDates(isin);
      const dates: string[] = res?.valuationDates || [];
      setValuationDates(dates);
      const applied = (formData.esg_valuation_date as string) || '';
      setSelectedDate((prev) => {
        if (prev && dates.includes(prev)) return prev;
        if (applied && dates.includes(applied)) return applied;
        return dates[0] || '';
      });
      if (dates.length === 0) {
        setMessage({ type: 'info', text: 'No ESG snapshots for this ISIN.' });
      }
    } catch (err: unknown) {
      const apiErr = err as { msg?: string };
      const detail = apiErr?.msg || (err instanceof Error ? err.message : 'Request failed');
      setMessage({
        type: 'error',
        text: `Could not load valuation dates (${detail}). Check factsheet access or contact support.`,
      });
    } finally {
      setDatesLoading(false);
    }
  }, [isin, formData.esg_valuation_date]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  useEffect(() => {
    if (message?.type !== 'success' || !appliedBaselineRef.current) return;
    const baseline = appliedBaselineRef.current;
    const currentSig = tableSignature(formData.business_table);
    if (selectedDate !== baseline.valuationDate || currentSig !== baseline.tableSig) {
      setMessage(null);
      appliedBaselineRef.current = null;
    }
  }, [formData.business_table, selectedDate, message]);

  const handleApply = async () => {
    if (!document?._id || !selectedDate) {
      setMessage({ type: 'error', text: 'Select a valuation date.' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const result = await apis.esgFund.applySnapshotToDocument(document._id, selectedDate);
      if (!result?.applied) {
        setMessage({ type: 'error', text: result?.message || 'Could not apply ESG data.' });
        return;
      }

      const snapshot = result.snapshot;
      const business_table = (snapshot?.criteria || []).map((c: any) => ({
        image: c.icon,
        label: c.label,
        no_turnover: c.noRevenue,
        sales_volume: c.revenueBelow5,
        esg_key: c.key,
      }));

      const exposure = (snapshot?.criteria || [])
        .filter((c: any) => c.hasExposureAbove5)
        .map((c: any) => ({ key: c.key, label: c.label }));

      onApplied({
        business_table,
        esg_valuation_date: selectedDate,
        esg_coverage_percent: snapshot?.coveragePercent ?? null,
        esg_exposure_flags: exposure,
      });

      appliedBaselineRef.current = {
        valuationDate: selectedDate,
        tableSig: tableSignature(business_table),
      };
      setMessage({ type: 'success', text: SUCCESS_PERSIST_MSG });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.msg || 'Failed to apply ESG data.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isin) {
    return (
      <Box sx={{ mt: 2, pt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Set ISIN in master data to import from CSV.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2, pt: 2 }}>
      <Divider sx={{ my: 16 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', my: 16 }}>
        Import from CSV
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={8} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={datesLoading}>
          <InputLabel>Valuation date</InputLabel>
          <Select
            label="Valuation date"
            value={selectedDate}
            onChange={(e) => {
              const next = e.target.value;
              setSelectedDate(next);
              if (
                message?.type === 'success' &&
                appliedBaselineRef.current &&
                next !== appliedBaselineRef.current.valuationDate
              ) {
                setMessage(null);
                appliedBaselineRef.current = null;
              }
            }}
          >
            {valuationDates.length === 0 && (
              <MenuItem value="" disabled>
                No snapshots
              </MenuItem>
            )}
            {valuationDates.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          onClick={handleApply}
          disabled={loading || datesLoading || !selectedDate}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          Apply from CSV
        </Button>
      </Stack>

      {exposureFlags.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
          Revenue ≥ 5%: {exposureFlags.map((f) => f.label).join(', ')}
        </Alert>
      )}

      {message && (
        <Alert
          severity={message.type === 'error' ? 'error' : message.type === 'success' ? 'success' : 'info'}
          sx={{ mt: 1.5, py: 0 }}
        >
          {message.text}
        </Alert>
      )}
    </Box>
  );
};
